/**
 * View Contexts — single source of truth
 */

import type { LayoutId } from './layouts';

export interface ViewContextSeed {
  /** The fixture account for this surface must have SF opportunities (for stage grouping). */
  needsOpportunities?: boolean;
  /** Minimum artifacts per blueprint kind on this surface's account for tests to pass. */
  minArtifactsPerKind: number;
  /** When true, post-synthesis validation ensures every INTENT_REGISTRY entry has a blueprint. */
  needsIntentCoverage?: boolean;
}

export interface ViewContextEntry {
  id: string;
  label: string;
  icon: string;
  basePath: string;
  /** Full URL template for E2E routing (e.g., '/accounts/{accountId}/activity'). */
  e2eRouteTemplate: string;
  allowedViewTypes: LayoutId[];
  defaultViewType: LayoutId;
  /** When true, the E2E matrix generates sub-routes from INTENT_REGISTRY for this context. */
  intentSubRoutes?: boolean;
  /** E2E seed requirements — what data this surface needs to render in tests. */
  seed: ViewContextSeed;
}

export const VIEW_CONTEXTS: readonly ViewContextEntry[] = [
  { id: 'categories', label: 'Categories', icon: '|||', basePath: '/categories', e2eRouteTemplate: '/categories', allowedViewTypes: ['cards', 'table'], defaultViewType: 'cards', seed: { minArtifactsPerKind: 1, needsIntentCoverage: true }, intentSubRoutes: true },
  { id: 'accounts', label: 'Accounts', icon: '@', basePath: '/accounts', e2eRouteTemplate: '/accounts/{accountId}/activity', allowedViewTypes: ['cards', 'table'], defaultViewType: 'cards', seed: { minArtifactsPerKind: 3 } },
  { id: 'pipeline', label: 'Pipeline', icon: '>>', basePath: '/pipeline', e2eRouteTemplate: '/pipeline/{accountId}', allowedViewTypes: ['cards', 'table'], defaultViewType: 'cards', seed: { needsOpportunities: true, minArtifactsPerKind: 1 } },
];
export type ViewContext = (typeof VIEW_CONTEXTS)[number]['id'];

export function getViewContext(id: string): ViewContextEntry {
  return VIEW_CONTEXTS.find(c => c.id === id) || VIEW_CONTEXTS[0];
}

/** Allowed view types = ViewContext ∩ Preset layouts. */
export function getAllowedViewTypes(contextId: string, presetLayouts: LayoutId[]): LayoutId[] {
  const ctx = getViewContext(contextId);
  return ctx.allowedViewTypes.filter(vt => presetLayouts.includes(vt));
}
